import React from "react";
import { StandardPageWrapper } from "../../components/layout/StandardPageWrapper";
import { PageHeader } from "../../components/layout/header/Header";
import {
  DashboardCompletionStepBox,
  ExpandablePanel,
  VerticalProgressBarCirclesWithText,
} from "@truevault/aquilo";
import { RouteHelpers, Routes } from "../../root/routes";
import { usePrimaryOrganization, usePrimaryOrganizationId } from "../../hooks";
import { useOnboardingProgress } from "../../../common/hooks/useOnboardingProgress";
import { useWorkflowSetupNavItems } from "../requestInstructions/WorkflowSetupVerticalNav";
import type { WorkflowNavItem } from "../requestInstructions/WorkflowSetupVerticalNav";
import { useOrgRegulationRequestTypes } from "../../hooks/useOrgRegulationRequestTypes";
import { WorkflowType } from "../../../common/service/server/dto/RegulationRequestTypeDto";

export const DevInstructionsFinishedApiIntegrationsSetupSlug = "finished-api-integrations-setup";
export const DevInstructionsFinishedWorkflowSetupSlug = "finished-workflow-setup";
export const DevInstructionsFinishedDataStorageSettingsSetupSlug =
  "finished-data-storage-settings-setup";

export const isWorkflowSetupComplete = (
  workflowNavItems: WorkflowNavItem[],
  isFinishedWorkflowSetupOnboarding: boolean,
) => isFinishedWorkflowSetupOnboarding && !workflowNavItems.some((item) => item.showSetUpBadge);

export const getWorkflowSetupRoute = (
  workflowNavItems: WorkflowNavItem[],
  isFinishedWorkflowSetupOnboarding: boolean,
  isLimitSettingsComplete: boolean,
) =>
  isFinishedWorkflowSetupOnboarding &&
  workflowNavItems.some((item) => item.tabKey === "request-to-limit") &&
  !isLimitSettingsComplete
    ? Routes.organization.requestHandling.requestToLimit
    : Routes.organization.requestHandling.base;

export default function WorkflowsDashboardPage() {
  const orgId = usePrimaryOrganizationId();
  const [org] = usePrimaryOrganization();

  // Force unlock of all nav items after org has completed onboarding latch (POL-7563)
  const forceUnlocked = org?.finishedOnboardingLatch === true;

  const [onboardingProgress] = useOnboardingProgress(orgId);
  const { filterByWorkflowType, loading: requestTypesLoading } = useOrgRegulationRequestTypes();
  const {
    workflowsSetupOnboardingProgress: {
      isFinishedWithApiIntegrationsSetup,
      isFinishedDataStorageOnboarding,
      isFinishedWorkflowSetupOnboarding,
      isLimitSettingsComplete,
    },
  } = onboardingProgress || {};

  const workflowNavItems = useWorkflowSetupNavItems({
    selectedTabKey: "consumer-lookup",
    hasRequestTypesForWorkflow: (workflowType) =>
      requestTypesLoading ? true : filterByWorkflowType(workflowType as WorkflowType).length > 0,
    isLimitStepIncomplete: !isLimitSettingsComplete && isFinishedWorkflowSetupOnboarding,
    isFinishedWorkflowSetupOnboarding,
  });
  let isDataStorageItemEnabled =
    forceUnlocked || isFinishedWithApiIntegrationsSetup || isFinishedDataStorageOnboarding;

  // Workflow setup is only complete when onboarding was finished and no workflow
  // remains in a "Set Up" state (for example, Limit follow-up setup).
  let isFinishedWithWorkflowSetup = isWorkflowSetupComplete(
    workflowNavItems,
    isFinishedWorkflowSetupOnboarding,
  );

  let isWorkflowSetupItemEnabled =
    forceUnlocked || isFinishedWithWorkflowSetup || isFinishedDataStorageOnboarding;

  // If this org has already completed the onboarding setup for workflows, but they have a missing response for the limit workflow, then take them to the limit workflow setup page directly
  let defaultWorkflowSettingsRoute = getWorkflowSetupRoute(
    workflowNavItems,
    isFinishedWorkflowSetupOnboarding,
    isLimitSettingsComplete,
  );

  let isEmailTemplatesItemEnabled = forceUnlocked || isFinishedWorkflowSetupOnboarding;

  let workflowSetupItem = (
    <DashboardCompletionStepBox
      iconFilename="/assets/images/stay_compliant/privacy_center/icons/request-workflows-icon.svg"
      title="Workflow Setup"
      description="Automate your request processing"
      status={(() => {
        if (isFinishedWithWorkflowSetup) {
          return "complete";
        }

        return forceUnlocked || isFinishedDataStorageOnboarding ? "finish-set-up" : "disabled";
      })()}
    />
  );

  let dataStorageSettingsItem = (
    <DashboardCompletionStepBox
      iconFilename="/assets/images/stay_compliant/privacy_center/icons/data-storage-icon.svg"
      title="Data Storage"
      description="Set up your vendors data access settings"
      status={(() => {
        if (isFinishedDataStorageOnboarding) {
          return "complete";
        }
        return isDataStorageItemEnabled ? "finish-set-up" : "disabled";
      })()}
    />
  );

  let emailTemplatesItem = (
    <DashboardCompletionStepBox
      iconFilename="/assets/images/stay_compliant/privacy_center/icons/email-templates-icon.svg"
      title="Email Templates"
      description="View and customize your message templates"
      status={
        isFinishedWorkflowSetupOnboarding
          ? "complete"
          : forceUnlocked
          ? "finish-set-up"
          : "disabled"
      }
    />
  );

  return (
    <StandardPageWrapper fullWidth>
      <PageHeader titleContent="DSAR Workflows" />
      <div className="tw-boundary">
        {(!isFinishedWithWorkflowSetup ||
          !isFinishedDataStorageOnboarding ||
          !isFinishedWithApiIntegrationsSetup) &&
          // Always hide onboarding panel after org has completed onboarding latch (POL-7563)
          !org?.finishedOnboardingLatch && (
            <ExpandablePanel title="Finish Setting Up DSAR Workflows" titleFontSize="2xl">
              <>
                <p className="font-normal text-[18px] text-neutral-800 mt-6">
                  Complete the steps below to set up your requests workflow.
                </p>
                <div className="mt-6">
                  <VerticalProgressBarCirclesWithText
                    steps={[
                      {
                        name: "API Integrations",
                        description: "Connect your vendors to TrueVault",
                        status: isFinishedWithApiIntegrationsSetup ? "complete" : "current",
                        route: Routes.privacyCenter.integrations,
                      },
                      {
                        name: "Data Storage",
                        description: "Set up your vendors data access settings",
                        status: isFinishedDataStorageOnboarding
                          ? "complete"
                          : isFinishedWithApiIntegrationsSetup
                          ? "current"
                          : "upcoming",
                        route: Routes.workflows.dataStorageSettings,
                      },
                      {
                        name: "Workflow Setup",
                        description: "Automate your request processing",
                        status: isFinishedWithWorkflowSetup
                          ? "complete"
                          : isFinishedDataStorageOnboarding
                          ? "current"
                          : "upcoming",
                        route: RouteHelpers.organization.requestHandling.base(orgId),
                      },
                    ]}
                  />
                </div>
              </>
            </ExpandablePanel>
          )}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "16px",
            maxWidth: "830px",
            marginTop: "48px",
          }}
        >
          <a href={Routes.privacyCenter.integrations}>
            <DashboardCompletionStepBox
              iconFilename="/assets/images/stay_compliant/privacy_center/icons/api-integrations-icon.svg"
              title="API Integrations"
              description="Connect your vendors to TrueVault"
              status={isFinishedWithApiIntegrationsSetup ? "complete" : "finish-set-up"}
            />
          </a>

          {isDataStorageItemEnabled ? (
            <a href={Routes.workflows.dataStorageSettings}>{dataStorageSettingsItem}</a>
          ) : (
            dataStorageSettingsItem
          )}

          {isWorkflowSetupItemEnabled ? (
            <a href={defaultWorkflowSettingsRoute}>{workflowSetupItem}</a>
          ) : (
            workflowSetupItem
          )}

          {isEmailTemplatesItemEnabled ? (
            <a href={Routes.workflows.emailTemplates}>{emailTemplatesItem}</a>
          ) : (
            emailTemplatesItem
          )}
        </div>
      </div>
    </StandardPageWrapper>
  );
}
